import React, { useContext, useEffect, useState } from 'react';
import { useHistory } from "react-router-dom";
import _ from 'lodash';
import PropTypes from 'prop-types';
import { ViewModelForm, ViewModelServiceContext } from '@xengage/gw-portals-viewmodel-react';
import { FormattedMessage, useTranslator } from '@jutro/locale'
import { MLLOBDisplayService } from 'gw-capability-gateway';
import { MLNotificationBlockUtil } from 'ml-portals-util-react';

import { MLLineTypeUtil, MLProductCodeUtil } from 'ml-portals-util-js'
import { CurrencyValue } from '@jutro/components';
import moment from 'moment';
import appConfig from 'app-config';
import classNames from 'classnames';

import metadata from './MLPolicySummaryDetailsComponent.metadata.json5';
import messages from './MLPolicySummaryDetailsComponent.messages';
import styles from './MLPolicySummaryDetailsComponent.module.scss';

const termTypeLabels = {
    Annual: messages.yearTerm,
    HalfYear: messages.sixMonthsTerm,
    '7Days': messages.sevenDaysTerm
};

const { MLProducerTypes } = appConfig;

const MLPolicySummaryDetailsComponent = (props) => {
    const { policies, isBundled, dtoInfo, totalFees, itemizedFees, hideSectionStyle, isDocumentsPage, numberActionRequiredDocs } = props;

    const translator = useTranslator();
    const history = useHistory();
    const viewModelService = useContext(ViewModelServiceContext);
    const [policiesVM, setPoliciesVM] = useState({});

    const {
        clearBlockAlerts
    } = MLNotificationBlockUtil();

    useEffect(() => {
         clearBlockAlerts('voidPayment')
    }, []);
    
    useEffect(() => {
        // clearBlockAlerts('voidPayment');

        if (!_.isEmpty(policies)) {
            policies.forEach((policy) => {
                policy.policyTotalCost = policy.paymentPlan_ML ? policy.paymentPlan_ML.total : (policy.totalCost || policy.totalCost_ML);
                setPoliciesVM((prevState) => {
                    return {
                        ...prevState,
                        [policy.policyNumber]: viewModelService.create({
                            serviceModel_ML: policy.serviceModel_ML,
                            leadsIndicator_ML: policy.leadsIndicator_ML
                        }, 'pc', dtoInfo)
                    }
                });
            });
        }
    }, [dtoInfo, policies, viewModelService]);

    const isPersonalAuto = (policy) => {
        return policy.policyLines.some((line) => MLLineTypeUtil.isPersonalAuto(line.lineOfBusinessCode));
    };

    const isHomeowners = (policy) => {
        return policy.policyLines.some((line) => MLLineTypeUtil.isHomeOwners(line.lineOfBusinessCode));
    };

    const getCoveragePartType = (policy) => {
        return policy.product ? policy.product.coveragePartType_ML : _.get(policy, 'lobs_ML.homeowners.coveragePartType_ML');
    };

    const isLandlordHome = (policy) => {
        return isHomeowners(policy) &&
            MLProductCodeUtil.isHomeOwnersPartialTypeLandlordHome(getCoveragePartType(policy));
    };

    const isLandlordCondo = (policy) => {
        return isHomeowners(policy) &&
            MLProductCodeUtil.isHomeOwnersPartialTypeLandlordCondo(getCoveragePartType(policy));
    };

    const isGcsProducer = (producerCode) => {
        return producerCode.producerType_ML === MLProducerTypes.GCS;
    };

    const getPolicyStatus = (policy) => {
        const expiredPolicyStatus = 'Expired';
        const withdrawnPolicyStatus = 'Withdrawn';
        let displayStatus;
        if (policy.canceled || policy.isCancelled || policy.displayStatus === withdrawnPolicyStatus) {
            displayStatus = translator(messages.cancelled);
        } else if (policy.displayStatus === expiredPolicyStatus) {
            displayStatus = translator(messages.expired);
        } else {
            displayStatus = translator(messages.active);
        }
        return displayStatus;
    };

    const getPoliciesOverrides = () => {
        const LOB = MLLOBDisplayService.LOB;
        const getLobIconClassName = (policy) => {
            switch (policy.policyLines[0].lineOfBusinessCode) {
                case LOB.PERSONAL_AUTO: {
                    return 'icon-icon-car-white';
                }
                case LOB.UMBRELLA: {
                    return 'icon-foremost-umbrella-icon';
                }
                case LOB.HOMEOWNERS: {
                    return 'icon-ml-home-detailed-icon';
                }
                default: {
                    break;
                }
            }
        }
        const getTypeKeyDescription = (typeKeyName) => {
            return translator({
                id: typeKeyName,
                defaultMessage: typeKeyName
            });
        };

        const formatPhoneNumber = (number) => {
            if (_.isNil(number) || number.length !== 10) {
                return number;
            }

            const area = number.substring(0, 3);
            const front = number.substring(3, 6);
            const end = number.substring(6, 10);
            return `(${area}) ${front}-${end}`;
        }

        const getProducerGroupAddressDetail = (policy) => {
            const city = _.get(policy, 'producerCode_ML.group_ML.address.city');
            const state = _.isEmpty(_.get(policy, 'producerCode_ML.group_ML.address.state')) ? '' : _.get(policy, 'producerCode_ML.group_ML.address.state');
            const zipCode = _.isEmpty(_.get(policy, 'producerCode_ML.group_ML.address.postalCode')) ? '' : _.get(policy, 'producerCode_ML.group_ML.address.postalCode');
            const addressDetail = !_.isEmpty(city) ? `${city}, ${state} ${zipCode}` : `${state} ${zipCode}`;
            return addressDetail;
        }

         const getDisplayName = (policy) => {
            return policy.isModernProduct_Ext ? translator(messages.namedNonOwnerNP) : translator(messages.namedNonOwner)    
        }

        const overrides = policies.map((policy, index) => {
            return {
                [`policyContainer${index}`]: {
                    className: !hideSectionStyle && 'ml-section'
                },
                [`lobIcon${index}`]: {
                    className: classNames('floatLeft', getLobIconClassName(policy))
                },
                [`protectiveOrderContainer${index}`]: {
                    visible: policy.protectiveOrder_ML
                },
                [`policyHeader${index}`]: {
                    content: translator(messages.policy, {lob: MLLOBDisplayService.getLOBPolicyName(policy), state: MLLOBDisplayService.getPolicyLabelState(policy)})
                },
                [`policyNumber${index}`]: {
                    content: translator(messages.policyNumber, {number: policy.policyNumber, leads: _.get(policiesVM, `[${policy.policyNumber}].leadsIndicator_ML.value.code`, '').toUpperCase()})
                },
                [`offerings${index}`]: {
                    visible:!policy.isModernProduct_Ext && !isLandlordCondo(policy) && !isLandlordHome(policy),
                    content:  policy.coveragePackageLevel_Ext 
                },
                [`bundledContingenciesContainer${index}`]: {
                    visible: !!policy.bundleID_ML || policy.hasOutstandingContingencies_ML
                },
                [`bundledPill${index}`]: {
                    visible: !!policy.bundleID_ML
                },
                [`contingenciesPill${index}`]: {
                    visible: policy.hasOutstandingContingencies_ML
                },
                [`accountNameGAF${index}`]: {
                    visible: !_.isNil(policy.accountNameGAF_ML),
                    content: translator(messages.groupName, {name: policy.accountNameGAF_ML})
                },
                [`accountCodeGAF${index}`]: {
                    visible: !_.isNil(policy.accountCodeGAF_ML),
                    content: translator(messages.gpcCode, {code: policy.accountCodeGAF_ML})
                },
                [`paypointNameGAF${index}`]: {
                    visible: !_.isNil(policy.paypointNameGAF_ML),
                    content: translator(messages.paypointName, {name: policy.paypointNameGAF_ML})
                },
                [`paypointCodeGAF${index}`]: {
                    visible: !_.isNil(policy.paypointCodeGAF_ML),
                    content: translator(messages.paypointCode, {code: policy.paypointCodeGAF_ML})
                },
                [`fullTermCost${index}`]: {
                    currency: policy.fullTermCost_Ext.currency,
                    value: policy.fullTermCost_Ext.amount,
                    tag: 'span',
                    visible: policy.fullTermCost_Ext !== undefined
                },
                [`fullTermCostTermType${index}`]: {
                    content: translator(termTypeLabels[policy.termType_ML])
                },
                [`policyTotalCostContainer${index}`]: {
                    visible: policy.fullTermCost_Ext !== undefined && (_.get(policy, 'policyTotalCost.amount') !== _.get(policy, 'fullTermCost_Ext.amount'))
                },
                [`policyTotalCost${index}`]: {
                    currency: _.get(policy, 'policyTotalCost.currency'),
                    value: _.get(policy, 'policyTotalCost.amount'),
                    tag: 'span'
                },
                [`policyTotalCostTermType${index}`]: {
                    content: translator(termTypeLabels[policy.termType_ML])
                },
                [`vehicleDetailsContainer${index}`]: {
                    visible: isPersonalAuto(policy)
                },
                [`namedNonOwner${index}`]: {
                    visible: policy.numberOfVehicles_ML === 0,
                    content:getDisplayName(policy),            
                },
                [`oneVehicle${index}`]: {
                    visible: policy.numberOfVehicles_ML === 1
                },
                [`manyVehicles${index}`]: {
                    visible: policy.numberOfVehicles_ML > 1,
                    content: translator(messages.vehicles, {number: policy.numberOfVehicles_ML})
                },
                [`addressDetailsContainer${index}`]: {
                    visible: policy.policyLines[0].lineOfBusinessCode === LOB.HOMEOWNERS
                },
                [`addressDetails${index}`]: {
                    content: (
                        <span>
                            {policy.address_ML.addressLine1}
                            <br />
                            {policy.address_ML.city}, {policy.address_ML.state} {policy.address_ML.postalCode}
                        </span>
                    )
                },
                [`excessLimitContainer${index}`]: {
                    visible: !_.isNil(policy.excessLimit_ML)
                },
                [`excessLimit${index}`]: {
                    content: (
                        <FormattedMessage
                            {...messages.excessLiability}
                            values={{
                                amount: <CurrencyValue tag="span" currency={_.get(policy, 'excessLimit_ML.currency')} value={_.get(policy, 'excessLimit_ML.amount')} />
                            }}
                        />
                    )
                },
                [`stateFeesComponent${index}`]: {
                    visible: totalFees ? totalFees.amount !== 0 : _.get(policy, 'totalChanges_Ext.amount', 0) !== 0,
                    totalFees: policy.fullTermCost_Ext !== undefined && (_.get(policy, 'policyTotalCost.amount') !== _.get(policy, 'fullTermCost_Ext.amount')) ? (policy.taxesAndFees || policy.totalChanges_Ext) : policy.totalFullTermChargesRPT_Ext,
                    itemizedFees: policy.fullTermCost_Ext !== undefined && (_.get(policy, 'policyTotalCost.amount') !== _.get(policy, 'fullTermCost_Ext.amount'))  ? (policy.feesCosts_Ext || itemizedFees) : policy.fullTermFeesCosts_Ext
                },
                [`serviceLevelLabel${index}`]: {
                    content: `${translator(messages.serviceLevel)} ${getTypeKeyDescription(_.get(policiesVM, `[${policy.policyNumber}].serviceModel_ML.value.name`))}`
                },
                [`docRetentionPill${index}`]: {
                    visible: policy.isDocRetention_Ext === true
                },
                [`producerName${index}`]: {
                    visible: _.get(policy, 'producerCode_ML.userDisplayName_ML.length', 0) > 0,
                    content: _.get(policy, 'producerCode_ML.userDisplayName_ML')
                },
                [`producerCode${index}`]: {
                    content: translator(messages.agentId, {agentId: policy.producerCodeOfService})
                },
                [`producerGroupDetailsContainer${index}`]: {
                    visible: policy.producerCode_ML.group_ML && !isGcsProducer(policy.producerCode_ML)
                },
                [`producerGroupName${index}`]: {
                    content: _.get(policy, 'producerCode_ML.group_ML.name')
                },
                [`producerGroupAddressLine1${index}`]: {
                    content: _.get(policy, 'producerCode_ML.group_ML.address.addressLine1')
                },
                [`producerGroupAddressLine2${index}`]: {
                    visible: _.get(policy, 'producerCode_ML.group_ML.address.addressLine2') !== undefined,
                    content: _.get(policy, 'producerCode_ML.group_ML.address.addressLine2')
                },
                [`producerGroupAddressDetails${index}`]: {
                    content: getProducerGroupAddressDetail(policy)
                },
                [`producerGroupPhoneNumber${index}`]: {
                    content: formatPhoneNumber(policy.producerCode_ML.group_ML.phoneNumber)
                },
                [`viewPolicyContainer${index}`]: {
                    visible: policy.canUserView
                },
                [`policyStatus${index}`]: {
                    content: `${translator(messages.status)} ${getPolicyStatus(policy)}`
                },
                [`policyEffectiveDate${index}`]: {
                    content: (
                        <FormattedMessage
                            {...messages.effectiveDate}
                            values={{
                                date: moment(policy.effective_ML).format('MM/DD/YYYY')
                            }}
                        />
                    )
                },
                [`policyExpirationDate${index}`]: {
                    content: (
                        <FormattedMessage
                            {...messages.expirationDate}
                            values={{
                                date: moment(policy.expiration_ML).format('MM/DD/YYYY')
                            }}
                        />
                    )
                },
                [`viewPolicyButton${index}`]:{
                    visible: (policy.canUserView === true),
                    mlTagManagerLinkClick: "Account Summary_View Policy",
                    onClick:(()=>{
                        history.push(`/policies/${policy.policyNumber}/summary`);
                    })
                }
            }
        });

        return Object.assign({}, ...overrides)
    }

    const overrideProps = {
        ...getPoliciesOverrides(),
        policiesIterable: {
            data: policies
        }
    }

    const resolvers = {
        resolveClassNameMap: styles
    };

    return (
        <ViewModelForm
            uiProps={metadata.componentContent}
            overrideProps={overrideProps}
            classNameMap={resolvers.resolveClassNameMap}
        />
    )
};

MLPolicySummaryDetailsComponent.propTypes = {
    policies: PropTypes.arrayOf(PropTypes.shape({})),
    dtoInfo: PropTypes.string,
    isBundled: PropTypes.bool,
    totalFees: PropTypes.shape({}),
    itemizedFees: PropTypes.arrayOf(PropTypes.shape({})),
    displayAlternativeTemplate: PropTypes.bool,
    hideSectionStyle: PropTypes.bool,
    isDocumentsPage: PropTypes.bool,
    numberActionRequiredDocs: PropTypes.number
};

MLPolicySummaryDetailsComponent.defaultProps = {
    policies: [],
    dtoInfo: 'edge.capabilities.gateway.policy.dto.PolicySummaryDTO',
    isBundled: false,
    totalFees: undefined,
    itemizedFees: undefined,
    displayAlternativeTemplate: undefined,
    hideSectionStyle: false,
    isDocumentsPage: false
};

export default MLPolicySummaryDetailsComponent;